import {Trans, useTranslation} from "react-i18next";
import React, {useEffect, useMemo, useRef} from "react";
import {usePriceList} from "../../../../Providers/PriceListProvider";
import useResizeObserver from "../../../hooks/useResizeObserver";
import IconButton from "@material-ui/core/IconButton";
import EpmsImage from "../../../../Common/EpmsImage";
import makeStyles from "@material-ui/core/styles/makeStyles";
import EditIcon from '@material-ui/icons/Edit';
import useImagePreview from "../../../hooks/useImagePreview";
import Icon from "@material-ui/core/Icon";
import {Link} from "react-router-dom";
import {formatCurrency} from "../../../../../utils";
import {Radio} from "@material-ui/core";
import useEpmsImage from "../../../hooks/useEpmsImage";
import ImageLoader from "../../../../Common/ImageLoader";


const useStyles = makeStyles(theme => ({
    orderPartContainer: {
        backgroundColor: 'white',
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1),
        boxShadow: '0px 2px 4px #00000029',
        '&.selected': {
            backgroundColor: theme.palette.secondary.light
        },
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    productNameContainer: {
        padding: theme.spacing(1),
        border: '1px solid transparent'
    },
    overflowLayer: {
        position: 'absolute',
        backgroundColor: 'rgba(255, 0, 0, 0.5)'
    },
    productName: {
        flexGrow: 1,
        minWidth: 0,
        flexBasis: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'flex',
        justifyContent: 'center'
    },
    linePrice: {
        marginLeft: theme.spacing(2)
    },
    orderPartLink: {
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'black',
        flexGrow: 1
    },
    orderPartTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
        '& > div': {
            display: 'flex',
            alignItems: 'center'
        },
        '& .MuiIcon-root': {
            fontSize: '1.4em',
            marginRight: theme.spacing(1)
        },
        '& .MuiButton-root': {
            padding: 0,
            textTransform: 'none',
            fontWeight: 'normal'
        },
        '& .material-icons': {
            fontSize: '1.2rem',
        }
    },
    orderPartBottomRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
    },
    orderPartImageContainer: {
        overflow: 'hidden',
        position: 'relative',
        flexBasis: '40%',
        marginRight: theme.spacing(1),
        flexGrow: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up('sm')]: {
            //paddingTop: '100%'
        }
    },
    orderPartImage: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        flexGrow: 1
    },
    closeButton: {
        position: 'relative',
        left: theme.spacing(1),
    },
    deleted: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '16px',
        fontWeight: 'bold'
    },
    qualityWarning: {
        position: 'absolute',
        color: 'yellow',
        right: 0,
        bottom: 0,
    }
}));

const PaperCopyOrderLine = ({orderLine, onDelete, scrollToItemUuid, selectedItems, selectMode, onToggleSelected}) => {
    const {t} = useTranslation();
    const {getProductByUuid } = usePriceList();
    const classes = useStyles();
    const ref = useRef();

    const isSelected = useMemo( () => {
        return selectedItems.indexOf(orderLine.uuid) >= 0;
    }, [selectedItems])

    // If we load the component and we're the current one:
    useEffect(() => {
        console.log('Scroll to item uuid', scrollToItemUuid, orderLine.uuid);
        if (ref.current && scrollToItemUuid == orderLine.uuid) {
            /* FIXME We have to wait a render for this to work? Or is it just the images not beeing loaded, so we scroll
             the wrong place? */
            setTimeout( () => {
                // We still here?
                if (ref.current) {
                    ref.current.scrollIntoView();
                }
            }, 0);
        }
    }, [scrollToItemUuid]);

    const product = useMemo(() => {
        return getProductByUuid(orderLine.product.uuid);
    }, [orderLine]);

    const handleClick = (e) => {
        if (selectMode) {
            onToggleSelected(e, orderLine);
            e.preventDefault();
        }
    }

    console.log('Got the order line', orderLine);

    return <div className={`${classes.orderPartContainer} ${isSelected ? 'selected' : ''}`} ref={ref}>
        <Link to={`/cart/edit/${orderLine.uuid}`} className={classes.orderPartLink} onClick={handleClick}>
            <div className={classes.orderPartTopRow}>
                {selectMode && <div><Radio checked={isSelected} color='secondary' size='small' style={{padding: 0}} /></div>}
                {!selectMode && <div><IconButton size='small' color='primary'>
                                    <Icon>edit</Icon>
                                </IconButton></div>}
                <div className={classes.productName}>
                    {t('%d pcs %s', {postProcess: 'sprintf', sprintf: [orderLine.quantity, product.name] })}
                    <span className={classes.linePrice}>{orderLine.dirty ? '...' : formatCurrency(orderLine.actualLinePrice)}</span>
                </div>
                {!selectMode &&
                    <div>
                        <IconButton className={classes.closeButton} color='primary' size={'small'} onClick={(e) => onDelete(e, orderLine)}>
                            <Icon>close</Icon></IconButton>
                    </div>}
            </div>
            <div className={classes.orderPartImageContainer} >
                <ProductPreviewImage product={product} orderLine={orderLine} />
            </div>
        </Link>
    </div>
}

const ProductPreviewImage = ({orderLine, product}) => {
    const classes = useStyles();
    const containerRef = useRef();
    const { isWhiteBorderProduct } = usePriceList();

    const options = useMemo ( () => {
        return {
            size: 640,
            effect: orderLine.image.filter
        }
    }, [orderLine.image.filter])
    const {loading, url} = useEpmsImage(orderLine.files[0].uuid, options)

    const cropRect = orderLine.image.cropRect || null;
    const adaption = orderLine.image.adaption ? 'fit-in' : 'fill-in';

    const containerSize = useResizeObserver(containerRef);

    const whiteBorder = useMemo(() => {
        return isWhiteBorderProduct(product);
    }, [product]);

    // Memo is used to return the same object every time, so useImagePreview isn't going berserk:
    const cs = useMemo(() => {
        return {width: containerSize.width - 36, height: containerSize.width - 36};
    }, [containerSize])

    const file = orderLine.files[0];

    const {paper, imageContainer, image, qualityWarning} = useImagePreview(file.size, cs,
        product, cropRect, whiteBorder, adaption);

    const paperStyle = {
        width: paper.width + 'px',
        height: paper.height + 'px',
        left: 0,
        right: 0,
        marginRight: 'auto',
        marginLeft: 'auto',
        position: 'absolute',
        backgroundColor: 'white',
    }


    const paperStyleWithShadow = {...paperStyle, filter: 'drop-shadow(0px 0px 1px rgba(0,0,0,.3)) drop-shadow(0px 0px 10px rgba(0,0,0,.3))'}

    const imageContainerStyle = {
        position: 'absolute',
        width: imageContainer.width + 'px',
        height: imageContainer.height + 'px',
        top: imageContainer.x + 'px',
        left: imageContainer.y + 'px',
        overflow: 'hidden',
        /* Hack to make Safari hide overflow */
        zIndex: 0
    }

    const imageOverflowContainerStyle = {
        position: 'absolute',
        width: imageContainer.width + 'px',
        height: imageContainer.height + 'px',
        top: imageContainer.x + 'px',
        left: imageContainer.y + 'px',
        opacity: 0.5
    }

    const imageStyle = {
        position: 'absolute',
        width: image.width + 'px',
        height: image.height + 'px',
        top: image.y + 'px',
        left: image.x + 'px'
    }

    return <div className={classes.orderPartImage} ref={containerRef}>
            <div style={{position: 'relative', height: paper.height + 'px', width: paper.width + 'px'}}>
                <div style={paperStyle} className="orderline-paper">
                    <div style={imageOverflowContainerStyle} className="orderline-paper-crop">
                        <img style={imageStyle} src={url} />
                        <div style={imageStyle} className={classes.overflowLayer}/>
                    </div>
                </div>
                <div style={paperStyleWithShadow}>
                    <div style={imageContainerStyle}>
                        <img style={imageStyle} src={url} />
                    </div>
                </div>
                {qualityWarning && <IconButton className={classes.qualityWarning}><Icon>warning</Icon></IconButton>}
            </div>
            {loading && <ImageLoader />}
            {orderLine.deleted && <div className={classes.deleted}><Trans>Marked for deletion</Trans></div>}
    </div>
}

export default PaperCopyOrderLine;